import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { useAuth } from "@/lib/auth-context";
import { bootstrapAdmin } from "@/lib/rpc/bootstrap.functions";
import { toast } from "sonner";

const search = z.object({
  redirect: z.string().optional(),
  reason: z.enum(["account_disabled"]).optional(),
});

export const Route = createFileRoute("/login")({
  validateSearch: search,
  component: LoginPage,
});

type FocusField = "username" | "password" | null;

function LoginPage() {
  const { signIn, user, role, loading, profile } = useAuth();
  const nav = useNavigate();
  const { redirect, reason } = useSearch({ from: "/login" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [focused, setFocused] = useState<FocusField>(null);

  useEffect(() => {
    bootstrapAdmin().catch(() => {});
  }, []);

  useEffect(() => {
    if (!loading && user) {
      if (profile && profile.status !== "active") {
        nav({ to: "/account-disabled" });
        return;
      }
      if (profile?.mfa_enabled && sessionStorage.getItem("mfa_passed") !== "1") {
        nav({ to: "/mfa-challenge" });
        return;
      }
      const dest =
        redirect ?? (role === "admin" || role === "moderator" ? "/admin/overview" : "/dashboard");
      nav({ to: dest });
    }
  }, [loading, user, profile?.status, profile?.mfa_enabled, role, redirect, nav]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const { error } = await signIn(email.trim(), password);
    setBusy(false);
    if (error) toast.error(error);
    else toast.success("Welcome back");
  };

  const eyeLStyle =
    focused === "username"
      ? { left: "0.75em", top: "1.12em" }
      : { left: "0.6em", top: "0.6em" };
  const eyeRStyle =
    focused === "username"
      ? { right: "0.75em", top: "1.12em" }
      : { right: "0.6em", top: "0.6em" };
  const handLStyle =
    focused === "password"
      ? { height: "6.56em", top: "3.87em", left: "11.75em", transform: "rotate(-155deg)" }
      : { height: "2.81em", top: "8.4em", left: "7.5em", transform: "rotate(0deg)" };
  const handRStyle =
    focused === "password"
      ? { height: "6.56em", top: "3.87em", right: "11.75em", transform: "rotate(155deg)" }
      : { height: "2.81em", top: "8.4em", right: "7.5em", transform: "rotate(0deg)" };

  return (
    <div className="panda-login-page">
      <style>{pandaCss}</style>
      <div className="panda-container">
        <form onSubmit={onSubmit}>
          {reason === "account_disabled" && (
            <div className="panda-alert">
              Your account has been disabled. Contact your admin.
            </div>
          )}
          <label htmlFor="username">Email:</label>
          <input
            type="email"
            id="username"
            placeholder="Email here..."
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={() => setFocused("username")}
            onBlur={() => setFocused((f) => (f === "username" ? null : f))}
          />
          <label htmlFor="password">Password:</label>
          <input
            type="password"
            id="password"
            placeholder="Password here..."
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onFocus={() => setFocused("password")}
            onBlur={() => setFocused((f) => (f === "password" ? null : f))}
          />
          <button type="submit" disabled={busy}>
            {busy ? "Signing in…" : "Login"}
          </button>
          <div className="panda-forgot">
            <Link to="/forgot-password">Forgot password?</Link>
          </div>
        </form>
        <div className="ear-l"></div>
        <div className="ear-r"></div>
        <div className="panda-face">
          <div className="blush-l"></div>
          <div className="blush-r"></div>
          <div className="eye-l">
            <div className="eyeball-l" style={eyeLStyle}></div>
          </div>
          <div className="eye-r">
            <div className="eyeball-r" style={eyeRStyle}></div>
          </div>
          <div className="nose"></div>
          <div className="mouth"></div>
        </div>
        <div className="hand-l" style={handLStyle}></div>
        <div className="hand-r" style={handRStyle}></div>
        <div className="paw-l"></div>
        <div className="paw-r"></div>
      </div>
    </div>
  );
}

const pandaCss = `
.panda-login-page {
  min-height: 100vh;
  background-color: #ffffff;
  font-family: "Poppins", sans-serif;
  position: relative;
  overflow: hidden;
}
.panda-login-page * { box-sizing: border-box; }
.panda-container {
  height: 31.25em;
  width: 31.25em;
  position: absolute;
  transform: translate(-50%, -50%);
  top: 50%;
  left: 50%;
}
.panda-container form {
  width: 23.75em;
  min-height: 18.75em;
  background-color: #ffffff;
  position: absolute;
  transform: translate(-50%, -50%);
  top: calc(50% + 3.1em);
  left: 50%;
  padding: 1.5em 3.1em;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-radius: 0.5em;
}
.panda-container form label {
  display: block;
  margin-bottom: 0.2em;
  font-weight: 600;
  color: #2e0d30;
}
.panda-container form input {
  font-size: 0.95em;
  font-weight: 400;
  color: #3f3554;
  padding: 0.3em;
  border: none;
  border-bottom: 0.12em solid #3f3554;
  outline: none;
  background: transparent;
  margin-bottom: 0.9em;
}
.panda-container form input:focus { border-color: #59cd49; }
.panda-container form button {
  font-size: 0.95em;
  padding: 0.8em 0;
  border-radius: 2em;
  border: none;
  cursor: pointer;
  outline: none;
  background-color: #7C3AED;
  color: #ffffff;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.15em;
  margin-top: 0.8em;
  transition: 0.5s;
}
.panda-container form button:disabled { opacity: 0.6; cursor: not-allowed; }
.panda-forgot { text-align: center; margin-top: 0.75em; font-size: 0.75em; }
.panda-forgot a { color: #3f3554; text-decoration: none; }
.panda-forgot a:hover { color: #59cd49; }
.panda-alert {
  background: #ffe5ec; color: #b00020; border-radius: 0.4em;
  padding: 0.5em 0.75em; font-size: 0.75em; margin-bottom: 0.75em;
}
.panda-face {
  height: 7.5em; width: 8.4em; background-color: #ffffff;
  border: 0.18em solid #2e0d30; border-radius: 7.5em 7.5em 5.62em 5.62em;
  position: absolute; top: 2em; margin: auto; left: 0; right: 0;
}
.panda-container .ear-l, .panda-container .ear-r {
  background-color: #3f3554; height: 2.5em; width: 2.81em;
  border: 0.18em solid #2e0d30; border-radius: 2.5em 2.5em 0 0;
  top: 1.75em; position: absolute;
}
.panda-container .ear-l { transform: rotate(-38deg); left: 10.75em; }
.panda-container .ear-r { transform: rotate(38deg); right: 10.75em; }
.blush-l, .blush-r {
  background-color: #ff8bb1; height: 1em; width: 1.37em;
  border-radius: 50%; position: absolute; top: 4em;
}
.blush-l { transform: rotate(25deg); left: 1em; }
.blush-r { transform: rotate(-25deg); right: 1em; }
.eye-l, .eye-r {
  background-color: #3f3554; height: 2.18em; width: 2em;
  border-radius: 2em; position: absolute; top: 2.18em;
}
.eye-l { left: 1.37em; transform: rotate(-20deg); }
.eye-r { right: 1.37em; transform: rotate(20deg); }
.eyeball-l, .eyeball-r {
  height: 0.6em; width: 0.6em; background-color: #ffffff;
  border-radius: 50%; position: absolute; left: 0.6em; top: 0.6em;
  transition: 1s all;
}
.eyeball-l { transform: rotate(20deg); }
.eyeball-r { transform: rotate(-20deg); }
.nose {
  height: 1em; width: 1em; background-color: #3f3554;
  position: absolute; top: 4.37em; margin: auto; left: 0; right: 0;
  border-radius: 1.2em 0 0 0.25em; transform: rotate(45deg);
}
.nose:before {
  content: ""; position: absolute; background-color: #3f3554;
  height: 0.6em; width: 0.1em; transform: rotate(-45deg);
  top: 0.75em; left: 1em;
}
.mouth, .mouth:before {
  height: 0.75em; width: 0.93em; background-color: transparent;
  position: absolute; border-radius: 50%; box-shadow: 0 0.18em #3f3554;
}
.mouth { top: 5.31em; left: 3.12em; }
.mouth:before { content: ""; position: absolute; left: 0.87em; }
.hand-l, .hand-r {
  background-color: #3f3554; height: 2.81em; width: 2.5em;
  border: 0.18em solid #2e0d30; border-radius: 0.6em 0.6em 2.18em 2.18em;
  transition: 1s all; position: absolute; top: 8.4em;
}
.hand-l { left: 7.5em; }
.hand-r { right: 7.5em; }
.paw-l, .paw-r {
  background-color: #3f3554; height: 3.12em; width: 3.12em;
  border: 0.18em solid #2e0d30; border-radius: 2.5em 2.5em 1.2em 1.2em;
  position: absolute; top: 26.56em;
}
.paw-l { left: 10em; }
.paw-r { right: 10em; }
.paw-l:before, .paw-r:before {
  position: absolute; content: ""; background-color: #ffffff;
  height: 1.37em; width: 1.75em; top: 1.12em; left: 0.55em;
  border-radius: 1.56em 1.56em 0.6em 0.6em;
}
.paw-l:after, .paw-r:after {
  position: absolute; content: ""; background-color: #ffffff;
  height: 0.5em; width: 0.5em; border-radius: 50%;
  top: 0.31em; left: 1.12em;
  box-shadow: 0.87em 0.37em #ffffff, -0.87em 0.37em #ffffff;
}
@media screen and (max-width: 500px) {
  .panda-container { font-size: 14px; }
}
`;
